import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Plus, Trash2, Edit2, GitBranch } from "lucide-react";
import {
  fetchAllMarketingSequences,
  fetchMarketingCampaigns,
  createMarketingSequence,
  updateMarketingSequence,
  fetchMarketingSegments,
} from "../../../../../../redux/actions/marketingAction";
import { backendClient } from "../../../../../../api/backend";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const SequenceList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { allSequences, campaigns, segments, loading } = useSelector((state) => state.marketing);
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingSeq, setEditingSeq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", campaign_id: "", segment_ids: [] });
  const [editFormData, setEditFormData] = useState({ name: "", segment_ids: [] });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchAllMarketingSequences({ page: page + 1 }));
  }, [dispatch, page]);

  useEffect(() => {
    dispatch(fetchMarketingCampaigns({ page_size: 100 }));
    dispatch(fetchMarketingSegments());
  }, [dispatch]);

  const allSegmentIds = (segments || []).map((s) => s.id);

  // Create
  const isCreateAllSelected = formData.segment_ids.length === 0;
  const createFormValid = formData.name.trim() && formData.campaign_id;

  const handleCreateSegmentChange = (e) => {
    const val = e.target.value;
    // If "all" was just toggled on
    if (val.includes("__all__")) {
      setFormData({ ...formData, segment_ids: [] });
      return;
    }
    setFormData({ ...formData, segment_ids: typeof val === "string" ? val.split(",") : val });
  };

  const handleCreate = async () => {
    if (!createFormValid) return;
    setSubmitting(true);
    try {
      await dispatch(createMarketingSequence(formData.campaign_id, {
        name: formData.name,
        segment_ids: formData.segment_ids,
      }));
      setCreateOpen(false);
      setFormData({ name: "", campaign_id: "", segment_ids: [] });
      dispatch(fetchAllMarketingSequences({ page: page + 1 }));
    } finally {
      setSubmitting(false);
    }
  };

  // Edit
  const isEditAllSelected = editFormData.segment_ids.length === 0;
  const editFormValid = editFormData.name.trim();

  const handleOpenEdit = (e, seq) => {
    e.stopPropagation();
    setEditingSeq(seq);
    setEditFormData({
      name: seq.name || "",
      segment_ids: (seq.segments || []).map((s) => s.id),
    });
    setEditOpen(true);
  };

  const handleEditSegmentChange = (e) => {
    const val = e.target.value;
    if (val.includes("__all__")) {
      setEditFormData({ ...editFormData, segment_ids: [] });
      return;
    }
    setEditFormData({ ...editFormData, segment_ids: typeof val === "string" ? val.split(",") : val });
  };

  const handleUpdate = async () => {
    if (!editingSeq || !editFormValid) return;
    setSubmitting(true);
    try {
      await dispatch(updateMarketingSequence(editingSeq.id, {
        name: editFormData.name,
        segment_ids: editFormData.segment_ids,
      }));
      setEditOpen(false);
      setEditingSeq(null);
      dispatch(fetchAllMarketingSequences({ page: page + 1 }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (e, seq) => {
    e.stopPropagation();
    setDeleteTarget(seq);
  };

  const confirmDelete = async () => {
    await backendClient.deleteMarketingSequence(deleteTarget.id);
    setDeleteTarget(null);
    dispatch(fetchAllMarketingSequences({ page: page + 1 }));
  };

  const renderSegmentValue = (selectedIds, isAll) => {
    if (isAll) return t("mkt_all_contacts");
    return selectedIds
      .map((id) => (segments || []).find((s) => s.id === id)?.name || id)
      .join(", ");
  };

  const items = allSequences?.items || [];
  const total = allSequences?.total || 0;
  const campaignItems = campaigns?.items || [];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t("mkt_sequences")} ({total})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
        >
          {t("mkt_new_sequence")}
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t("common_name")}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t("mkt_campaign")}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t("mkt_segment")}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t("mkt_steps")}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t("mkt_created_at")}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t("mkt_actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <GitBranch size={32} color={theme.palette.text.secondary} style={{ marginBottom: 8 }} />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {t("mkt_no_sequences")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((seq) => (
                  <TableRow
                    key={seq.id}
                    hover
                    onClick={() => navigate(`/admin/marketing/sequences/${seq.id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {seq.name || t("mkt_default_sequence")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={seq.campaign_name || "\u2014"}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: "6px", fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      {(seq.segments || []).length > 0 ? (
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {seq.segments.map((seg) => (
                            <Chip
                              key={seg.id}
                              label={seg.name}
                              size="small"
                              sx={{
                                borderRadius: "6px",
                                fontSize: "0.65rem",
                                height: 20,
                                bgcolor: seg.color ? alpha(seg.color, 0.1) : alpha(theme.palette.primary.main, 0.1),
                                color: seg.color || theme.palette.primary.main,
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "0.8rem" }}>
                          {t("mkt_all_contacts")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{seq.steps_count}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}>
                        {new Date(seq.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={(e) => handleOpenEdit(e, seq)}>
                            <Edit2 size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => handleDelete(e, seq)}>
                            <Trash2 size={14} color={theme.palette.error?.main} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        )}
      </Paper>

      {/* Create Sequence Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t("mkt_new_sequence")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label={t("mkt_sequence_name")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              size="small"
              required
            />
            <FormControl size="small" fullWidth required>
              <InputLabel>{t("mkt_campaign")}</InputLabel>
              <Select
                value={formData.campaign_id}
                onChange={(e) => setFormData({ ...formData, campaign_id: e.target.value })}
                label={t("mkt_campaign")}
              >
                {campaignItems.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t("mkt_target_segments")}</InputLabel>
              <Select
                multiple
                value={isCreateAllSelected ? ["__all__"] : formData.segment_ids}
                onChange={handleCreateSegmentChange}
                label={t("mkt_target_segments")}
                renderValue={() => renderSegmentValue(formData.segment_ids, isCreateAllSelected)}
              >
                <MenuItem value="__all__">
                  <Checkbox checked={isCreateAllSelected} size="small" />
                  <ListItemText primary={t("mkt_all_contacts")} />
                </MenuItem>
                {(segments || []).map((seg) => (
                  <MenuItem key={seg.id} value={seg.id}>
                    <Checkbox checked={formData.segment_ids.indexOf(seg.id) > -1} size="small" />
                    <ListItemText primary={seg.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>{t("common_cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!createFormValid || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? t("mkt_creating") : t("mkt_create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sequence Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t("mkt_edit_sequence")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label={t("mkt_sequence_name")}
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              fullWidth
              size="small"
              required
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t("mkt_target_segments")}</InputLabel>
              <Select
                multiple
                value={isEditAllSelected ? ["__all__"] : editFormData.segment_ids}
                onChange={handleEditSegmentChange}
                label={t("mkt_target_segments")}
                renderValue={() => renderSegmentValue(editFormData.segment_ids, isEditAllSelected)}
              >
                <MenuItem value="__all__">
                  <Checkbox checked={isEditAllSelected} size="small" />
                  <ListItemText primary={t("mkt_all_contacts")} />
                </MenuItem>
                {(segments || []).map((seg) => (
                  <MenuItem key={seg.id} value={seg.id}>
                    <Checkbox checked={editFormData.segment_ids.indexOf(seg.id) > -1} size="small" />
                    <ListItemText primary={seg.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>{t("common_cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={!editFormValid || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? t("mkt_saving") : t("common_save")}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        itemName={deleteTarget?.name}
        itemType={t("mkt_sequence")}
      />
    </Box>
  );
};

export default SequenceList;
